import { useEffect, useRef } from "react";
import type { Map as MaplibreMap, GeoJSONSource } from "maplibre-gl";
import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import { cssColor, mapCameraTarget, type MapPoint } from "./directoryMapData";
import type { RoutePoint } from "./nearestNeighborRoute";

const MEXICO_CENTER: [number, number] = [-102, 23];
const LINE_SOURCE_ID = "route-line";
const STOPS_SOURCE_ID = "route-stops";
const START_SOURCE_ID = "route-start";
const FIT_PADDING = 48;
const FIT_MAX_ZOOM = 15;
const FIT_DURATION_MS = 400;

type RouteMapStop = RoutePoint & { id: number };

const lineFeatureCollection = (
  start: RoutePoint,
  stops: RouteMapStop[],
): FeatureCollection<LineString> => ({
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [start, ...stops].map((p) => [p.lng, p.lat]),
      },
    },
  ],
});

const stopsFeatureCollection = (
  stops: RouteMapStop[],
): FeatureCollection<Point, { seq: number }> => ({
  type: "FeatureCollection",
  features: stops.map(
    (p, i): Feature<Point, { seq: number }> => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { seq: i + 1 },
    }),
  ),
});

const startFeatureCollection = (
  start: RoutePoint,
): FeatureCollection<Point> => ({
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {},
      geometry: { type: "Point", coordinates: [start.lng, start.lat] },
    },
  ],
});

/** Camino de las paradas en memoria → forma de `MapPoint` solo para reusar `mapCameraTarget`. */
const asMapPoints = (points: RoutePoint[]): MapPoint[] =>
  points.map((p, i) => ({
    id: i,
    lat: p.lat,
    lng: p.lng,
    nombre: "",
    thermo_base: 0,
  }));

/**
 * Mapa de la ruta armada (Fase 2, paso 2 del plan): línea recta que une el
 * punto de partida con las paradas en el orden actual, y un círculo numerado
 * por parada. Misma base que `DirectoryMap` (OpenFreeMap + `import()` de
 * maplibre-gl), pero sin click-to-navigate: aquí solo se muestra el orden.
 */
export const RouteMap = ({
  start,
  stops,
}: {
  start: RoutePoint;
  stops: RouteMapStop[];
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const startRef = useRef(start);
  startRef.current = start;
  const stopsRef = useRef(stops);
  stopsRef.current = stops;

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let map: MaplibreMap | undefined;

    (async () => {
      const [{ Map: MaplibreMapCtor, NavigationControl, setWorkerUrl }] =
        await Promise.all([
          import("maplibre-gl"),
          import("maplibre-gl/dist/maplibre-gl.css"),
        ]);
      if (cancelled || !containerRef.current) return;

      if (import.meta.env.PROD) {
        setWorkerUrl(new URL("maplibre-gl-worker.mjs", import.meta.url).href);
      }

      map = new MaplibreMapCtor({
        container: containerRef.current,
        style: "https://tiles.openfreemap.org/styles/liberty",
        center: MEXICO_CENTER,
        zoom: 4.2,
      });
      map.addControl(new NavigationControl(), "top-right");
      map.on("load", () => {
        if (!map) return;
        const primary = cssColor("--primary");

        map.addSource(LINE_SOURCE_ID, {
          type: "geojson",
          data: lineFeatureCollection(startRef.current, stopsRef.current),
        });
        map.addLayer({
          id: LINE_SOURCE_ID,
          type: "line",
          source: LINE_SOURCE_ID,
          paint: { "line-color": primary, "line-width": 3 },
        });

        map.addSource(START_SOURCE_ID, {
          type: "geojson",
          data: startFeatureCollection(startRef.current),
        });
        map.addLayer({
          id: START_SOURCE_ID,
          type: "circle",
          source: START_SOURCE_ID,
          paint: {
            "circle-radius": 7,
            "circle-color": cssColor("--foreground"),
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });

        map.addSource(STOPS_SOURCE_ID, {
          type: "geojson",
          data: stopsFeatureCollection(stopsRef.current),
        });
        map.addLayer({
          id: `${STOPS_SOURCE_ID}-circle`,
          type: "circle",
          source: STOPS_SOURCE_ID,
          paint: {
            "circle-radius": 11,
            "circle-color": primary,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
        map.addLayer({
          id: `${STOPS_SOURCE_ID}-label`,
          type: "symbol",
          source: STOPS_SOURCE_ID,
          layout: {
            "text-field": ["to-string", ["get", "seq"]],
            "text-size": 12,
            "text-allow-overlap": true,
          },
          paint: { "text-color": "#ffffff" },
        });

        const target = mapCameraTarget(
          asMapPoints([startRef.current, ...stopsRef.current]),
        );
        if (target.kind === "multi") {
          map.fitBounds(target.bounds, {
            padding: FIT_PADDING,
            maxZoom: FIT_MAX_ZOOM,
            duration: FIT_DURATION_MS,
          });
        } else if (target.kind === "single") {
          map.flyTo({
            center: target.center,
            zoom: target.zoom,
            duration: FIT_DURATION_MS,
          });
        }
      });
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const lineSource = map.getSource(LINE_SOURCE_ID) as
      | GeoJSONSource
      | undefined;
    const stopsSource = map.getSource(STOPS_SOURCE_ID) as
      | GeoJSONSource
      | undefined;
    const startSource = map.getSource(START_SOURCE_ID) as
      | GeoJSONSource
      | undefined;
    lineSource?.setData(lineFeatureCollection(start, stops));
    stopsSource?.setData(stopsFeatureCollection(stops));
    startSource?.setData(startFeatureCollection(start));
  }, [start, stops]);

  return (
    <div
      ref={containerRef}
      role="application"
      aria-label="Mapa de la ruta"
      className="h-[50vh] w-full rounded-lg border md:h-[60vh]"
    />
  );
};
